import type * as Types from '../../../graphql/generated-base';

import type { DocumentNode } from 'graphql';
import * as Apollo from '@apollo/client';
import * as ApolloReactHooks from '@graphql/react-hooks';
export type GetRegistryDataQueryVariables = Types.Exact<{
  eventHandle: Types.Scalars['String'];
  withGuestSiteData: Types.Scalars['Boolean'];
  eventDesignPurpose: Types.EventDesignPurpose;
}>;

export type GetRegistryDataQuery = {
  __typename?: 'Query';
  eventByName?: {
    __typename?: 'Event';
    id: string;
    firebaseId?: string;
    eventDesign?: {
      __typename?: 'EventDesign';
      id: string;
      hasUnpublishedChanges: boolean;
      hasVisitedDesignPage?: boolean | null;
      guestSiteTarget: Types.GuestSiteTargetEnum;
      brannanNavigation: Types.BrannanNavigation;
      useCustomTheme: boolean;
      accent?: string | null;
      customCss: string;
      linkAndButtonColor?: string | null;
      artFontColor?: string | null;
      artBackgroundColor?: string | null;
      pageBackgroundColor?: string | null;
      pageFontColor?: string | null;
      websiteLayout: { __typename?: 'DesignLayout'; id: string; layoutType: Types.DesignLayoutType; name: string };
      font: { __typename?: 'Font'; id: string; display: string; fontFamily: string; textTransform: Types.FontWeight };
      fonts?: Array<{
        __typename?: 'FontPackConfiguration';
        id: string;
        category: Types.Category;
        size?: number | null;
        sizePercent?: number | null;
        font?: { __typename?: 'Font'; id: string; fontFamily: string; fontSize: number; fontWeight: number; firebaseFontKey: string } | null;
      }> | null;
      theme: {
        __typename?: 'Theme';
        id: string;
        themeId: string;
        name: string;
        colorSchemes: Array<{ __typename?: 'ThemeColorScheme'; id: string; colors: Array<{ __typename?: 'Color'; hex: string; isLight: boolean }> }>;
        designLayouts: Array<{ __typename?: 'DesignLayout'; id: string }>;
        websiteLayoutBrannanSettings?: {
          __typename?: 'WebsiteLayoutBrannanSettings';
          brannanBackgroundTransparency?: {
            __typename?: 'DesignPreferenceRange';
            supportsCustomization: boolean;
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            rangeValue: number;
            rangeMax: number;
            rangeMin: number;
            rangeStep: number;
          } | null;
        } | null;
        websiteLayoutAlohaSettings?: {
          __typename?: 'WebsiteLayoutAlohaSettings';
          alohaColorPreference?: {
            __typename?: 'DesignPreferenceColor';
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            supportsCustomization: boolean;
            colorValue: { __typename?: 'Color'; hex: string; isLight: boolean };
          } | null;
          alohaTextColorPreference?: {
            __typename?: 'DesignPreferenceColor';
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            supportsCustomization: boolean;
            colorValue: { __typename?: 'Color'; hex: string; isLight: boolean };
          } | null;
        } | null;
      };
      masthead: { __typename?: 'EventDesignMasthead'; id?: string | null; isEnabled: boolean };
      colorPalette: Array<{ __typename?: 'ColorSet'; id: string; color: { __typename?: 'Color'; hex: string; isLight: boolean } }>;
      activeWebsiteHeaderPresentationLayout?: { __typename?: 'WebsiteHeaderPresentationSettings'; dataJSON: any; schemaURL: string; websiteLayout: string; id: string } | null;
      customTheme?: {
        __typename?: 'CustomTheme';
        id: string;
        desktopThemeAsset?: { __typename?: 'CustomThemeAsset'; id: string; assetURL: string } | null;
        mobileThemeAsset?: { __typename?: 'CustomThemeAsset'; id: string; assetURL: string } | null;
      } | null;
      designPreferences: Array<
        | {
            __typename?: 'EventDesignPreferenceBoolean';
            id: string;
            featureId: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            featureValueType: Types.FeatureValueType;
            booleanValue: boolean;
          }
        | {
            __typename?: 'EventDesignPreferenceColorPointer';
            id: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            colorPointer?: number | null;
            colorPointerPosition?: Types.ColorPointerLocation | null;
          }
        | {
            __typename?: 'EventDesignPreferenceRange';
            id: string;
            featureId: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            featureValueType: Types.FeatureValueType;
            rangeValue: number;
          }
      >;
    } | null;
    info: {
      __typename?: 'EventInfo';
      id: string;
      eventDisplayName?: string | null;
      timezone?: string | null;
      finalizedDate?: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number } | null;
      dateMetadata?: {
        __typename?: 'EventDateMetadata';
        customText?: string | null;
        dateDisplayType: Types.DateDisplayType;
        dateFormat?: string | null;
        dateRangeFormat?: string | null;
      } | null;
      endDate?: { __typename?: 'Date'; timestamp: any; timezone: string; milliseconds: number } | null;
    };
    pages: Array<{
      __typename?: 'EventPage';
      id: string;
      type: Types.EventPageType;
      contentType?: Types.PageContentType | null;
      pageKind: Types.PageKind;
      pageSlug: string;
      pageTitle: string;
      disabled: boolean;
      private: boolean;
      visibility: Types.EventPageVisibility;
      isPageContainer: boolean;
      layoutType?: string | null;
      subTitle?: string | null;
      photoV2?:
        | {
            __typename?: 'DefaultPagePhoto';
            id: string;
            url: string;
            width: number;
            height: number;
            altTag?: string | null;
            layout: { __typename?: 'DefaultPagePhotoLayout'; alignX: string; alignY: string };
          }
        | {
            __typename?: 'Photo';
            id: string;
            url: string;
            width: number;
            height: number;
            altTag?: string | null;
            layout: { __typename?: 'PhotoLayout'; alignX: string; alignY: string };
            transform?: { __typename?: 'PhotoTransform'; x?: number | null; y?: number | null } | null;
          }
        | null;
      mobilePhoto?: {
        __typename?: 'Photo';
        id: string;
        url: string;
        width: number;
        height: number;
        altTag?: string | null;
        layout: { __typename?: 'PhotoLayout'; alignX: string; alignY: string };
        transform?: { __typename?: 'PhotoTransform'; x?: number | null; y?: number | null } | null;
      } | null;
      photoPresentation?: {
        __typename?: 'WebsitePagePhotoPresentation';
        id: string;
        presentationLayout: string;
        cropInfo?: { __typename?: 'WebsitePagePhotoPresentationCropInfo'; x: number; y: number; width: number; height: number; unit: string } | null;
      } | null;
    }>;
    welcomePage: Array<{
      __typename?: 'EventPage';
      id: string;
      type: Types.EventPageType;
      contentType?: Types.PageContentType | null;
      pageKind: Types.PageKind;
      pageSlug: string;
      pageTitle: string;
      disabled: boolean;
      private: boolean;
      visibility: Types.EventPageVisibility;
      isPageContainer: boolean;
      layoutType?: string | null;
      subTitle?: string | null;
      photoV2?:
        | {
            __typename?: 'DefaultPagePhoto';
            id: string;
            url: string;
            width: number;
            height: number;
            altTag?: string | null;
            layout: { __typename?: 'DefaultPagePhotoLayout'; alignX: string; alignY: string };
          }
        | {
            __typename?: 'Photo';
            id: string;
            url: string;
            width: number;
            height: number;
            altTag?: string | null;
            layout: { __typename?: 'PhotoLayout'; alignX: string; alignY: string };
            transform?: { __typename?: 'PhotoTransform'; x?: number | null; y?: number | null } | null;
          }
        | null;
      mobilePhoto?: {
        __typename?: 'Photo';
        id: string;
        url: string;
        width: number;
        height: number;
        altTag?: string | null;
        layout: { __typename?: 'PhotoLayout'; alignX: string; alignY: string };
        transform?: { __typename?: 'PhotoTransform'; x?: number | null; y?: number | null } | null;
      } | null;
      photoPresentation?: {
        __typename?: 'WebsitePagePhotoPresentation';
        id: string;
        presentationLayout: string;
        cropInfo?: { __typename?: 'WebsitePagePhotoPresentationCropInfo'; x: number; y: number; width: number; height: number; unit: string } | null;
      } | null;
    }>;
    registry?: {
      __typename?: 'EventData';
      id: string;
      event: { __typename?: 'Event'; id: string };
      summary?: { __typename?: 'EventRegistriesSummary'; cannotSync: boolean; lastSyncAt?: { __typename?: 'Date'; timestamp: any } | null } | null;
      currency?: { __typename?: 'RegistryCurrency'; isDefault: boolean; id: string; displayName: string; code: string; rank?: number | null } | null;
      shippingAddress?: {
        __typename?: 'ShippingAddress';
        name?: string | null;
        address1: string;
        address2?: string | null;
        phone?: string | null;
        city: string;
        state: string;
        postalCode?: string | null;
        country: string;
        countryCode?: string | null;
        validated: boolean;
        hash?: string | null;
      } | null;
      donationFundInfo: {
        __typename?: 'DonationFundInfo';
        defaultPlatform?: { __typename?: 'DonationFundPlatform'; id?: string | null; type: Types.DonationFundPlatformTypeEnum; username?: string | null } | null;
        defaultSuggestedDonationMonetaryValue?: { __typename?: 'MonetaryValue'; valueInMinorUnits: number } | null;
        platforms: Array<{ __typename?: 'DonationFundPlatform'; id?: string | null; type: Types.DonationFundPlatformTypeEnum; username?: string | null; link?: string | null }>;
        items: Array<{
          __typename?: 'DonationFund';
          id: string;
          platform?: { __typename?: 'DonationFundPlatform'; id?: string | null; type: Types.DonationFundPlatformTypeEnum } | null;
        }>;
      };
    } | null;
    eventUsers: Array<{ __typename?: 'EventUser'; id: string; status: Types.EventUserStatus }>;
  } | null;
  registryPageContainer?: { __typename?: 'PageContainer'; id: string; contentType?: Types.PageContentType | null; pageKind: Types.PageKind } | null;
};

export type GuestEventDetailsByNameQueryVariables = Types.Exact<{
  eventHandle: Types.Scalars['String'];
  pageType?: Types.InputMaybe<Types.EventPageType>;
  eventDesignPurpose: Types.EventDesignPurpose;
}>;

export type GuestEventDetailsByNameQuery = {
  __typename?: 'Query';
  eventByName?: {
    __typename?: 'Event';
    id: string;
    photoV2Url?: string | null;
    website: string;
    info: {
      __typename?: 'EventInfo';
      id: string;
      eventType: Types.EventType;
      fianceeFirstName?: string | null;
      eventDisplayName?: string | null;
      ownerFirstName?: string | null;
      ownerLastName?: string | null;
    };
    eventDesign?: {
      __typename?: 'EventDesign';
      id: string;
      hasUnpublishedChanges: boolean;
      hasVisitedDesignPage?: boolean | null;
      guestSiteTarget: Types.GuestSiteTargetEnum;
      brannanNavigation: Types.BrannanNavigation;
      useCustomTheme: boolean;
      accent?: string | null;
      customCss: string;
      linkAndButtonColor?: string | null;
      artFontColor?: string | null;
      artBackgroundColor?: string | null;
      pageBackgroundColor?: string | null;
      pageFontColor?: string | null;
      websiteLayout: { __typename?: 'DesignLayout'; id: string; layoutType: Types.DesignLayoutType; name: string };
      font: { __typename?: 'Font'; id: string; display: string; fontFamily: string; textTransform: Types.FontWeight };
      fonts?: Array<{
        __typename?: 'FontPackConfiguration';
        id: string;
        category: Types.Category;
        size?: number | null;
        sizePercent?: number | null;
        font?: { __typename?: 'Font'; id: string; fontFamily: string; fontSize: number; fontWeight: number; firebaseFontKey: string } | null;
      }> | null;
      theme: {
        __typename?: 'Theme';
        id: string;
        themeId: string;
        name: string;
        colorSchemes: Array<{ __typename?: 'ThemeColorScheme'; id: string; colors: Array<{ __typename?: 'Color'; hex: string; isLight: boolean }> }>;
        designLayouts: Array<{ __typename?: 'DesignLayout'; id: string }>;
        websiteLayoutBrannanSettings?: {
          __typename?: 'WebsiteLayoutBrannanSettings';
          brannanBackgroundTransparency?: {
            __typename?: 'DesignPreferenceRange';
            supportsCustomization: boolean;
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            rangeValue: number;
            rangeMax: number;
            rangeMin: number;
            rangeStep: number;
          } | null;
        } | null;
        websiteLayoutAlohaSettings?: {
          __typename?: 'WebsiteLayoutAlohaSettings';
          alohaColorPreference?: {
            __typename?: 'DesignPreferenceColor';
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            supportsCustomization: boolean;
            colorValue: { __typename?: 'Color'; hex: string; isLight: boolean };
          } | null;
          alohaTextColorPreference?: {
            __typename?: 'DesignPreferenceColor';
            id: string;
            featureName: string;
            featureValueType: Types.FeatureValueType;
            supportsCustomization: boolean;
            colorValue: { __typename?: 'Color'; hex: string; isLight: boolean };
          } | null;
        } | null;
      };
      masthead: { __typename?: 'EventDesignMasthead'; id?: string | null; isEnabled: boolean };
      colorPalette: Array<{ __typename?: 'ColorSet'; id: string; color: { __typename?: 'Color'; hex: string; isLight: boolean } }>;
      activeWebsiteHeaderPresentationLayout?: { __typename?: 'WebsiteHeaderPresentationSettings'; dataJSON: any; schemaURL: string; websiteLayout: string; id: string } | null;
      customTheme?: {
        __typename?: 'CustomTheme';
        id: string;
        desktopThemeAsset?: { __typename?: 'CustomThemeAsset'; id: string; assetURL: string } | null;
        mobileThemeAsset?: { __typename?: 'CustomThemeAsset'; id: string; assetURL: string } | null;
      } | null;
      designPreferences: Array<
        | {
            __typename?: 'EventDesignPreferenceBoolean';
            id: string;
            featureId: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            featureValueType: Types.FeatureValueType;
            booleanValue: boolean;
          }
        | {
            __typename?: 'EventDesignPreferenceColorPointer';
            id: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            colorPointer?: number | null;
            colorPointerPosition?: Types.ColorPointerLocation | null;
          }
        | {
            __typename?: 'EventDesignPreferenceRange';
            id: string;
            featureId: string;
            featureName: string;
            featureType: Types.DesignFeatureType;
            featureValueType: Types.FeatureValueType;
            rangeValue: number;
          }
      >;
    } | null;
    pages: Array<{ __typename?: 'EventPage'; id: string; disabled: boolean; type: Types.EventPageType; pageSlug: string }>;
    photo?: { __typename?: 'Photo'; id: string; url: string } | null;
    pageInfo?: {
      __typename?: 'EventPageInfo';
      id: string;
      page: {
        __typename?: 'EventPage';
        id: string;
        photoV2?: { __typename?: 'DefaultPagePhoto'; id: string; url: string } | { __typename?: 'Photo'; id: string; url: string } | null;
      };
    } | null;
  } | null;
};

export const GetRegistryDataDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetRegistryData' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventHandle' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'withGuestSiteData' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventDesignPurpose' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPurpose' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventByName' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'name' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventHandle' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Event' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'firebaseId' },
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'include' },
                            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'if' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'withGuestSiteData' } } }],
                          },
                        ],
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'eventDesign' },
                        arguments: [
                          { kind: 'Argument', name: { kind: 'Name', value: 'purpose' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventDesignPurpose' } } },
                        ],
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesign' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'info' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'eventDisplayName' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'finalizedDate' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Date' } }] },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'dateMetadata' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'customText' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'dateDisplayType' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'dateFormat' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'dateRangeFormat' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'endDate' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Date' } }] },
                            },
                          ],
                        },
                      },
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryShippingAddressFragment' } },
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'DonationFundInfoFragment' } },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'pages' },
                        name: { kind: 'Name', value: 'pages' },
                        arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'admin' }, value: { kind: 'BooleanValue', value: true } }],
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventPage' } }] },
                      },
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'welcomePage' },
                        name: { kind: 'Name', value: 'pages' },
                        arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'type' }, value: { kind: 'EnumValue', value: 'welcome' } }],
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventPage' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'registry' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'event' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'summary' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'lastSyncAt' },
                                    selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'timestamp' } }] },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'cannotSync' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'currency' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventRegistryCurrency' } }] },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'eventUsers' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            alias: { kind: 'Name', value: 'registryPageContainer' },
            name: { kind: 'Name', value: 'pageContainerBySlug' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'payload' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    { kind: 'ObjectField', name: { kind: 'Name', value: 'eventName' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventHandle' } } },
                    { kind: 'ObjectField', name: { kind: 'Name', value: 'pageSlug' }, value: { kind: 'StringValue', value: 'registry', block: false } },
                  ],
                },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'options' },
                value: { kind: 'ObjectValue', fields: [{ kind: 'ObjectField', name: { kind: 'Name', value: 'admin' }, value: { kind: 'BooleanValue', value: true } }] },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contentType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'pageKind' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesign' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesign' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasUnpublishedChanges' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasVisitedDesignPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestSiteTarget' } },
          { kind: 'Field', name: { kind: 'Name', value: 'brannanNavigation' } },
          { kind: 'Field', name: { kind: 'Name', value: 'useCustomTheme' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'websiteLayout' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignLayout' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'font' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'display' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fontFamily' } },
                { kind: 'Field', name: { kind: 'Name', value: 'textTransform' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fonts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'font' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontFamily' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontSize' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firebaseFontKey' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'size' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizePercent' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'theme' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'themeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'colorSchemes' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ThemeColorScheme' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'designLayouts' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'websiteLayoutBrannanSettings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'brannanBackgroundTransparency' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceRange' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'websiteLayoutAlohaSettings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'alohaColorPreference' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceColor' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'alohaTextColorPreference' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceColor' } }] },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'accent' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'masthead' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEnabled' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customCss' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colorPalette' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ColorSet' } }] },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsiteEventColors' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeWebsiteHeaderPresentationLayout' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'useCustomTheme' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customTheme' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomTheme' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'designPreferences' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignLayout' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignLayout' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'layoutType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ThemeColorScheme' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ThemeColorScheme' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colors' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Color' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Color' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'hex' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isLight' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignPreferenceRange' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignPreferenceRange' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeMax' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeMin' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeStep' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignPreferenceColor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignPreferenceColor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colorValue' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ColorSet' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ColorSet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'color' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsiteEventColors' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesign' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'linkAndButtonColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'artFontColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'artBackgroundColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageBackgroundColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageFontColor' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', alias: { kind: 'Name', value: 'id' }, name: { kind: 'Name', value: 'eventId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dataJSON' } },
          { kind: 'Field', name: { kind: 'Name', value: 'schemaURL' } },
          { kind: 'Field', name: { kind: 'Name', value: 'websiteLayout' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomTheme' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomTheme' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'desktopThemeAsset' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assetURL' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mobileThemeAsset' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assetURL' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'colorPointer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'colorPointerPosition' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceRange' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeValue' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'booleanValue' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Date' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Date' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'timestamp' } },
          { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
          { kind: 'Field', name: { kind: 'Name', value: 'milliseconds' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryShippingAddressFragment' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Event' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registry' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'shippingAddress' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'address1' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'address2' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'countryCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'validated' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'hash' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'currency' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventRegistryCurrency' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventRegistryCurrency' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryCurrency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'RegistryCurrencyCodes' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDefault' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'RegistryCurrencyCodes' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'RegistryCurrency' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'displayName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'code' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DonationFundInfoFragment' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Event' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'registry' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'donationFundInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'defaultPlatform' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'username' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'defaultSuggestedDonationMonetaryValue' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'platforms' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'username' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'link' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'items' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'platform' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventPage' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventPage' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'contentType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageKind' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageSlug' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageTitle' } },
          { kind: 'Field', alias: { kind: 'Name', value: 'subTitle' }, name: { kind: 'Name', value: 'pageSubTitleV0918' } },
          { kind: 'Field', name: { kind: 'Name', value: 'disabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'private' } },
          { kind: 'Field', name: { kind: 'Name', value: 'visibility' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isPageContainer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'layoutType' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photoV2' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhotoV2WithoutBlurHash' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mobilePhoto' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhotoWithoutBlurHash' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'photoPresentation' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsitePagePhotoPresentation' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhotoV2WithoutBlurHash' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PagePhoto' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                { kind: 'Field', name: { kind: 'Name', value: 'width' } },
                { kind: 'Field', name: { kind: 'Name', value: 'height' } },
                { kind: 'Field', name: { kind: 'Name', value: 'altTag' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'layout' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhotoLayout' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'transform' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhotoTransform' } }] },
                },
              ],
            },
          },
          {
            kind: 'InlineFragment',
            typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DefaultPagePhoto' } },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                { kind: 'Field', name: { kind: 'Name', value: 'width' } },
                { kind: 'Field', name: { kind: 'Name', value: 'height' } },
                { kind: 'Field', name: { kind: 'Name', value: 'altTag' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'layout' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhotoLayoutV2' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhotoLayout' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhotoLayout' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'alignX' } },
          { kind: 'Field', name: { kind: 'Name', value: 'alignY' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhotoTransform' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'PhotoTransform' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'x' } },
          { kind: 'Field', name: { kind: 'Name', value: 'y' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhotoLayoutV2' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DefaultPagePhotoLayout' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'alignX' } },
          { kind: 'Field', name: { kind: 'Name', value: 'alignY' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PhotoWithoutBlurHash' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'url' } },
          { kind: 'Field', name: { kind: 'Name', value: 'width' } },
          { kind: 'Field', name: { kind: 'Name', value: 'height' } },
          { kind: 'Field', name: { kind: 'Name', value: 'altTag' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'layout' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhotoLayout' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'transform' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PhotoTransform' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsitePagePhotoPresentation' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'WebsitePagePhotoPresentation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'presentationLayout' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cropInfo' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsitePagePhotoPresentationCropInfo' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsitePagePhotoPresentationCropInfo' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'WebsitePagePhotoPresentationCropInfo' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'x' } },
          { kind: 'Field', name: { kind: 'Name', value: 'y' } },
          { kind: 'Field', name: { kind: 'Name', value: 'width' } },
          { kind: 'Field', name: { kind: 'Name', value: 'height' } },
          { kind: 'Field', name: { kind: 'Name', value: 'unit' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useGetRegistryDataQuery__
 *
 * To run a query within a React component, call `useGetRegistryDataQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetRegistryDataQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetRegistryDataQuery({
 *   variables: {
 *      eventHandle: // value for 'eventHandle'
 *      withGuestSiteData: // value for 'withGuestSiteData'
 *      eventDesignPurpose: // value for 'eventDesignPurpose'
 *   },
 * });
 */
export function useGetRegistryDataQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<GetRegistryDataQuery, GetRegistryDataQueryVariables>) {
  return ApolloReactHooks.useQuery<GetRegistryDataQuery, GetRegistryDataQueryVariables>(GetRegistryDataDocument, baseOptions);
}
export function useGetRegistryDataLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<GetRegistryDataQuery, GetRegistryDataQueryVariables>) {
  return ApolloReactHooks.useLazyQuery<GetRegistryDataQuery, GetRegistryDataQueryVariables>(GetRegistryDataDocument, baseOptions);
}
export type GetRegistryDataQueryHookResult = ReturnType<typeof useGetRegistryDataQuery>;
export type GetRegistryDataLazyQueryHookResult = ReturnType<typeof useGetRegistryDataLazyQuery>;
export type GetRegistryDataQueryResult = Apollo.QueryResult<GetRegistryDataQuery, GetRegistryDataQueryVariables>;
export const GuestEventDetailsByNameDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GuestEventDetailsByName' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventHandle' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageType' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'EventPageType' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventDesignPurpose' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPurpose' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventByName' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'name' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventHandle' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'info' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fianceeFirstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventDisplayName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'ownerFirstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'ownerLastName' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'eventDesign' },
                  arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'purpose' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventDesignPurpose' } } }],
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesign' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pages' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'disabled' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'pageSlug' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'photoV2Url' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pageInfo' },
                  arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'type' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'pageType' } } }],
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'page' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'photoV2' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'InlineFragment',
                                    typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Photo' } },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'InlineFragment',
                                    typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DefaultPagePhoto' } },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'website' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesign' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesign' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasUnpublishedChanges' } },
          { kind: 'Field', name: { kind: 'Name', value: 'hasVisitedDesignPage' } },
          { kind: 'Field', name: { kind: 'Name', value: 'guestSiteTarget' } },
          { kind: 'Field', name: { kind: 'Name', value: 'brannanNavigation' } },
          { kind: 'Field', name: { kind: 'Name', value: 'useCustomTheme' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'websiteLayout' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignLayout' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'font' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'display' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fontFamily' } },
                { kind: 'Field', name: { kind: 'Name', value: 'textTransform' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fonts' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'font' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontFamily' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontSize' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fontWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firebaseFontKey' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'size' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sizePercent' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'theme' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'themeId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'colorSchemes' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ThemeColorScheme' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'designLayouts' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'websiteLayoutBrannanSettings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'brannanBackgroundTransparency' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceRange' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'websiteLayoutAlohaSettings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'alohaColorPreference' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceColor' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'alohaTextColorPreference' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'DesignPreferenceColor' } }] },
                      },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'accent' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'masthead' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEnabled' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'customCss' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colorPalette' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ColorSet' } }] },
          },
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsiteEventColors' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'activeWebsiteHeaderPresentationLayout' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'useCustomTheme' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'customTheme' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'CustomTheme' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'designPreferences' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } }] },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignLayout' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignLayout' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'layoutType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ThemeColorScheme' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ThemeColorScheme' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colors' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'Color' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Color' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'hex' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isLight' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignPreferenceRange' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignPreferenceRange' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeMax' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeMin' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeStep' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'DesignPreferenceColor' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'DesignPreferenceColor' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supportsCustomization' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'colorValue' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ColorSet' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ColorSet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'color' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'Color' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsiteEventColors' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesign' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'linkAndButtonColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'artFontColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'artBackgroundColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageBackgroundColor' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageFontColor' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'WebsiteHeaderPresentationSettings' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', alias: { kind: 'Name', value: 'id' }, name: { kind: 'Name', value: 'eventId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'dataJSON' } },
          { kind: 'Field', name: { kind: 'Name', value: 'schemaURL' } },
          { kind: 'Field', name: { kind: 'Name', value: 'websiteLayout' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CustomTheme' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CustomTheme' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'desktopThemeAsset' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assetURL' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'mobileThemeAsset' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'assetURL' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceColorPointer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'colorPointer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'colorPointerPosition' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceRange' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceRange' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rangeValue' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'EventDesignPreferenceBoolean' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'featureValueType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'booleanValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useGuestEventDetailsByNameQuery__
 *
 * To run a query within a React component, call `useGuestEventDetailsByNameQuery` and pass it any options that fit your needs.
 * When your component renders, `useGuestEventDetailsByNameQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGuestEventDetailsByNameQuery({
 *   variables: {
 *      eventHandle: // value for 'eventHandle'
 *      pageType: // value for 'pageType'
 *      eventDesignPurpose: // value for 'eventDesignPurpose'
 *   },
 * });
 */
export function useGuestEventDetailsByNameQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<GuestEventDetailsByNameQuery, GuestEventDetailsByNameQueryVariables>) {
  return ApolloReactHooks.useQuery<GuestEventDetailsByNameQuery, GuestEventDetailsByNameQueryVariables>(GuestEventDetailsByNameDocument, baseOptions);
}
export function useGuestEventDetailsByNameLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<GuestEventDetailsByNameQuery, GuestEventDetailsByNameQueryVariables>) {
  return ApolloReactHooks.useLazyQuery<GuestEventDetailsByNameQuery, GuestEventDetailsByNameQueryVariables>(GuestEventDetailsByNameDocument, baseOptions);
}
export type GuestEventDetailsByNameQueryHookResult = ReturnType<typeof useGuestEventDetailsByNameQuery>;
export type GuestEventDetailsByNameLazyQueryHookResult = ReturnType<typeof useGuestEventDetailsByNameLazyQuery>;
export type GuestEventDetailsByNameQueryResult = Apollo.QueryResult<GuestEventDetailsByNameQuery, GuestEventDetailsByNameQueryVariables>;
